import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { StripeService } from 'ngx-stripe';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map, of, switchMap, catchError, throwError } from 'rxjs';

import { PaymentIntent, PaymentIntentResult } from '@stripe/stripe-js';
import * as formatDataHelpers from 'src/app/modules/dashboard/helpers/format-subscription-data.helpers';
import { API_URL_V4, environment } from 'src/environments/environment.local';
import { PaymentMethod as KinoPaymentMethod } from '../models/payment-method.model';
import { Store } from '@ngrx/store';
import { selectFamilyOption } from 'app/store/products/products.selectors';
import { SubscriptionPlan } from 'app/models/subscrition-plan.model';
import { CartActions } from '../store/cart/cart.actions';
import { selectOrderCoupon } from '../store/order/order.selectors';

@Injectable({ providedIn: 'root' })
export class StripeApi {
  private http: HttpClient = inject(HttpClient);
  private stripeService: StripeService = inject(StripeService);
  private translate: TranslateService = inject(TranslateService);
  private store: Store = inject(Store);

  private readonly stripeUrl: string = environment.api.dashboard.stripe;

  getPaymentMethods$(): Observable<KinoPaymentMethod[]> {
    return this.http.get(`${this.stripeUrl}/paymentMethod`).pipe(
      map((response: any) => {
        return formatDataHelpers.convertApiDataToPaymentMethods(response.data);
      })
    );
  }

  postPaymentMethod$({
    paymentId,
  }: {
    paymentId: string;
  }): Observable<KinoPaymentMethod> {
    return this.http
      .post(`${this.stripeUrl}/paymentMethod`, { paymentId })
      .pipe(
        map((response: any) => {
          return formatDataHelpers.convertApiDataToPaymentMethod(response);
        })
      );
  }

  deletePaymentMethod$(paymentId: string): Observable<any> {
    const url: string = `${this.stripeUrl}/paymentMethod`;
    return this.http.delete(url, { params: { paymentId } });
  }

  subscribeToPlan$({
    productId,
    paymentId,
  }: {
    productId: string;
    paymentId: string;
  }): Observable<any> {
    return this.use3DSecureConfirmationIfNeeded(
      this.http.post(`${this.stripeUrl}/subscribe`, {
        productId,
        paymentId,
      })
    );
  }

  migratePlan$({ targetPlanId }: { targetPlanId: string }): Observable<any> {
    return this.use3DSecureConfirmationIfNeeded(
      this.http.post(`${this.stripeUrl}/migrate`, {
        targetSubscriptionId: targetPlanId,
      })
    );
  }

  resumeSubscription$({
    subscriptionId,
  }: {
    subscriptionId: number;
  }): Observable<any> {
    return this.use3DSecureConfirmationIfNeeded(
      this.http.post(`${this.stripeUrl}/resume`, {
        subscriptionId,
      })
    );
  }

  private use3DSecureConfirmationIfNeeded(
    paymentApiCall: Observable<any>
  ): Observable<any> {
    return paymentApiCall.pipe(
      switchMap((res: any) => {
        if (!res.next_action) {
          return of(res);
        }

        const clientSecret: string = res.client_secret;
        return this.stripeService
          .confirmCardPayment(clientSecret, {
            setup_future_usage: 'off_session',
          })
          .pipe(
            map((paymentIntentResult: PaymentIntentResult) => {
              if (!paymentIntentResult || paymentIntentResult.error) {
                const errorMessage: string = this.translate.instant(
                  'dashboard.subscription.authentication3DSecure.error'
                );
                throw Error(errorMessage);
              } else {
                return res;
              }
            })
          );
      })
    );
  }

  cancelSubscription$({
    subscriptionId,
  }: {
    subscriptionId: number;
  }): Observable<any> {
    if (!subscriptionId) {
      throw Error('No subscription to cancel.');
    }
    return this.http.post(`${this.stripeUrl}/cancel`, {
      subscriptionId,
    });
  }

  initPaymentIntent$(productId: string): Observable<PaymentIntent> {
    return this.store.select(selectOrderCoupon).pipe(
      switchMap((coupon: any) => {
        return this.http.post<PaymentIntent>(`${this.stripeUrl}/subscribe`, {
          productId,
          couponId: coupon?.id || null,
        });
      })
    );
  }

  initGiftCardPaymentIntent$(priceId: string): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(`${this.stripeUrl}/buyGiftCard`, {
      priceId,
    });
  }

  cancelPaymentIntent$(paymentIntendId: string): Observable<PaymentIntent> {
    return this.http.post<PaymentIntent>(
      `${this.stripeUrl}/cancelPaymentIntent`,
      {
        paymentIntendId: paymentIntendId,
      }
    );
  }

  initMigration$(subPlanId: string): Observable<any> {
    return this.http
      .post<any>(`${this.stripeUrl}/migrate`, {
        targetSubscriptionId: subPlanId,
      })
      .pipe(
        catchError((error) => {
          if (error.status === 402) {
            return of(error.error);
          }
          return throwError(() => error);
        })
      );
  }

  payFamilyOption$(paymentMethodId: string): Observable<any> {
    return this.store.select(selectFamilyOption).pipe(
      switchMap((familyOption: SubscriptionPlan) => {
        // 1. Initialiser le Payment Intent pour l'option famille
        this.store.dispatch(CartActions.setFamilyOption({ isFamily: false }));
        return this.initPaymentIntent$(familyOption.id).pipe(
          switchMap((paymentIntent: PaymentIntent) => {
            const clientSecret = paymentIntent.client_secret;
            if (!clientSecret)
              throw new Error('Secret client not available for family option.');

            // 2. Confirmer le paiement de l'option famille
            return this.stripeService
              .confirmCardPayment(clientSecret, {
                payment_method: paymentMethodId,
                setup_future_usage: 'off_session',
              })
              .pipe(
                map((paymentIntentResult: PaymentIntentResult) => {
                  if (!paymentIntentResult || paymentIntentResult.error) {
                    const errorMessage: string = 'Payment intent result error.';
                    throw new Error(errorMessage);
                  }
                  return paymentIntentResult;
                })
              );
          })
        );
      })
    );
  }

  retrieveCouponPromo$(coupon: string, currency: string): Observable<any> {
    const url: string = `${API_URL_V4}/subscription/stripe/coupon`;
    return this.http.get(url, { params: { coupon, currency } }).pipe(
      map((response: any) => {
        if (response.data.message) {
          return { message: response.data.message };
        }
        return { data: response.data };
      }),
      catchError((error) => {
        console.error('Error retrieving coupon:', error);
        return of({ message: 'Error retrieving coupon', key: error.error.key });
      })
    );
  }
}
